import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

// ── Scrolling Marquee ──
const marqueeMessages = [
  { flag: "🇺🇸", baby: "Baby Emma", msg: "Thank you! God bless your whole family forever. — Emma's Mom, USA" },
  { flag: "🇦🇺", baby: "Baby Oliver", msg: "Your kindness gave Oliver his first smile. We pray for you every day. — Oliver's Family, Australia" },
  { flag: "🇨🇦", baby: "Baby Lily", msg: "Lily sleeps warm now because of you. You are our angel. — Lily's Family, Canada" },
  { flag: "🇩🇪", baby: "Baby Lucas", msg: "Lucas laughed for the first time after your donation. God bless you! — Lucas's Family, Germany" },
  { flag: "🇬🇧", baby: "Baby Sophie", msg: "Sophie is growing strong and healthy. Your love changed our lives. — Sophie's Family, UK" },
  { flag: "🇳🇿", baby: "Baby Max", msg: "Max sends you a painted stone with our family's blessing. Thank you! — Max's Family, New Zealand" },
  { flag: "🇧🇷", baby: "Baby Luca", msg: "We drew a picture of our family for you. You are part of it now. — Luca's Family, Brazil" },
  { flag: "🇻🇳", baby: "Baby Mei", msg: "Mei is wrapped in warmth because of your generous heart. Bless you! — Mei's Family, Vietnam" },
  { flag: "🇰🇪", baby: "Baby Amara", msg: "Amara's first bracelet is for you — made with love and prayers. — Amara's Family, Kenya" },
  { flag: "🇪🇹", baby: "Baby Noah", msg: "Noah is healthy and smiling. Your donation was a miracle for us. — Noah's Family, Ethiopia" },
];

function ThankYouMarquee() {
  return (
    <div className="bg-gradient-to-r from-rose-600 via-rose-500 to-orange-500 py-3 overflow-hidden relative">
      <div className="flex animate-[marquee_40s_linear_infinite] whitespace-nowrap">
        {[...marqueeMessages, ...marqueeMessages].map((item, i) => (
          <span key={i} className="inline-flex items-center gap-2 mx-8 text-white text-sm font-medium flex-shrink-0">
            <span className="text-base">{item.flag}</span>
            <span className="text-rose-200 font-bold">{item.baby}:</span>
            <span className="text-white/90">{item.msg}</span>
            <span className="text-rose-300 mx-2">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Share & Spread Love Floating Button ──
function ShareFloatingButton() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const shareUrl = typeof window !== "undefined" ? window.location.origin + "/#infant-donation-form" : "https://starbookprime.com";
  const shareText = "Help infants around the world get love, milk & clothes. Every donor receives a handmade gift from the infant's family. God bless you! 🍼❤️";

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const platforms = [
    {
      name: "Facebook",
      icon: "ri-facebook-circle-fill",
      color: "bg-[#1877F2] hover:bg-[#166FE5]",
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
    },
    {
      name: "Twitter / X",
      icon: "ri-twitter-x-fill",
      color: "bg-gray-900 hover:bg-gray-800",
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    },
    {
      name: "WhatsApp",
      icon: "ri-whatsapp-fill",
      color: "bg-[#25D366] hover:bg-[#20BD5C]",
      url: `https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`,
    },
    {
      name: "Telegram",
      icon: "ri-telegram-fill",
      color: "bg-[#229ED9] hover:bg-[#1A8FC4]",
      url: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
    },
  ];

  return (
    <div className="fixed bottom-8 right-6 z-50 flex flex-col items-end gap-3">
      {/* Platform buttons */}
      {open && (
        <div className="flex flex-col items-end gap-2 animate-[fadeInUp_0.2s_ease]">
          {/* Copy link */}
          <button
            onClick={handleCopy}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-gray-200 text-gray-700 text-xs font-bold hover:border-rose-300 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className={copied ? "ri-check-line text-green-500" : "ri-link text-gray-500"} />
            {copied ? "Copied!" : "Copy Link"}
          </button>
          {platforms.map((p) => (
            <a
              key={p.name}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-white text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${p.color}`}
            >
              <i className={`${p.icon} text-base`} />
              {p.name}
            </a>
          ))}
          {/* Label */}
          <div className="text-xs text-gray-400 font-medium pr-1">Share &amp; Spread Love</div>
        </div>
      )}

      {/* Main FAB */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-14 h-14 flex items-center justify-center rounded-full bg-rose-500 text-white hover:bg-rose-600 transition-all hover:scale-110 cursor-pointer relative"
        style={{ boxShadow: "0 4px 20px rgba(244,63,94,0.4)" }}
      >
        <i className={`text-2xl transition-transform duration-200 ${open ? "ri-close-line rotate-90" : "ri-share-forward-fill"}`} />
        {!open && (
          <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center rounded-full bg-amber-400 text-amber-900 text-xs font-extrabold animate-bounce">
            <i className="ri-heart-fill text-xs" />
          </span>
        )}
      </button>
    </div>
  );
}

const donationAmounts = [5, 10, 25, 50, 100, 250];

const impactStats = [
  { icon: "ri-heart-fill", value: "12,480+", label: "Infants Helped", color: "text-rose-500", bg: "bg-rose-50" },
  { icon: "ri-gift-2-fill", value: "8,920+", label: "Gifts Exchanged", color: "text-orange-500", bg: "bg-orange-50" },
  { icon: "ri-global-line", value: "47", label: "Countries Reached", color: "text-amber-600", bg: "bg-amber-50" },
  { icon: "ri-hand-heart-line", value: "$2.1M+", label: "Total Donated", color: "text-green-600", bg: "bg-green-50" },
];

const recentDonors = [
  {
    name: "Michael R.",
    avatar: "https://public.readdy.ai/ai/img_res/e7d69558441aec57c8d745e95243051d.jpg",
    amount: "$250",
    message: "God bless every little one. May your family always have warmth and love.",
    gift: "Received a handmade bracelet from baby Amara's family in Kenya",
    badge: "Lottery Winner",
    badgeColor: "bg-yellow-100 text-yellow-700",
  },
  {
    name: "Jennifer L.",
    avatar: "https://readdy.ai/api/search-image?query=elegant%20woman%20philanthropist%20warm%20smile%20portrait%20soft%20natural%20background&width=80&height=80&seq=donor-av2&orientation=squarish",
    amount: "$100",
    message: "Every child deserves milk, clothes, and love. We are one big family.",
    gift: "Received a thank-you drawing from baby Luca's family in Brazil",
    badge: "Top Donor",
    badgeColor: "bg-orange-100 text-orange-700",
  },
  {
    name: "David K.",
    avatar: "https://readdy.ai/api/search-image?query=successful%20businessman%20generous%20smile%20portrait%20warm%20lighting%20clean%20background&width=80&height=80&seq=donor-av3&orientation=squarish",
    amount: "$500",
    message: "Sharing is the greatest blessing. These babies are our future.",
    gift: "Received a handwoven cloth from baby Mei's family in Vietnam",
    badge: "Angel Donor",
    badgeColor: "bg-rose-100 text-rose-700",
  },
];

// ── Updated: blonde-haired babies ──
const infantStories = [
  {
    name: "Baby Emma",
    age: "4 months",
    country: "USA",
    flag: "🇺🇸",
    image: "https://readdy.ai/api/search-image?query=adorable%20blonde%20hair%20baby%20girl%20infant%20happy%20smiling%20blue%20eyes%20chubby%20cheeks%20warm%20sunlight%20soft%20white%20background%20portrait%20cute%20joyful&width=400&height=400&seq=infant-blonde1&orientation=squarish",
    need: "Milk & Warm Clothes",
    status: "Helped",
    statusColor: "bg-green-100 text-green-700",
    message: "Emma's family sends their deepest gratitude and a handmade bracelet as a gift of love.",
  },
  {
    name: "Baby Oliver",
    age: "6 months",
    country: "Australia",
    flag: "🇦🇺",
    image: "https://readdy.ai/api/search-image?query=cute%20blonde%20hair%20baby%20boy%20infant%20smiling%20bright%20blue%20eyes%20happy%20portrait%20warm%20natural%20light%20clean%20white%20simple%20background%20chubby%20adorable&width=400&height=400&seq=infant-blonde2&orientation=squarish",
    need: "Formula & Diapers",
    status: "Helped",
    statusColor: "bg-green-100 text-green-700",
    message: "Oliver's mama drew a beautiful picture of their family to say thank you from the heart.",
  },
  {
    name: "Baby Lily",
    age: "3 months",
    country: "Canada",
    flag: "🇨🇦",
    image: "https://readdy.ai/api/search-image?query=adorable%20blonde%20hair%20baby%20girl%20infant%20peaceful%20sleeping%20wrapped%20in%20soft%20pink%20blanket%20warm%20cozy%20portrait%20simple%20white%20background%20sweet%20angelic&width=400&height=400&seq=infant-blonde3&orientation=squarish",
    need: "Clothes & Blankets",
    status: "Needs Help",
    statusColor: "bg-rose-100 text-rose-700",
    message: "Lily's family will weave a traditional cloth as a heartfelt gift for every donor.",
  },
  {
    name: "Baby Lucas",
    age: "5 months",
    country: "Germany",
    flag: "🇩🇪",
    image: "https://readdy.ai/api/search-image?query=beautiful%20blonde%20hair%20baby%20boy%20infant%20bright%20smile%20happy%20joyful%20portrait%20warm%20golden%20sunlight%20simple%20white%20background%20cute%20chubby%20cheeks&width=400&height=400&seq=infant-blonde4&orientation=squarish",
    need: "Milk & Medicine",
    status: "Needs Help",
    statusColor: "bg-rose-100 text-rose-700",
    message: "Lucas's family will send a handcrafted blessing card with a prayer for your whole family.",
  },
];

const howItWorks = [
  {
    icon: "ri-hand-coin-line",
    title: "You Donate",
    desc: "Lottery winners, rich hearts, and kind souls make a small donation — even $5 changes a life.",
    color: "bg-rose-50",
    iconColor: "text-rose-500",
    num: "1",
  },
  {
    icon: "ri-gift-2-line",
    title: "Infant Family Sends Gift",
    desc: "The infant's family sends you a small handmade gift — a bracelet, drawing, cloth, or blessing card.",
    color: "bg-orange-50",
    iconColor: "text-orange-500",
    num: "2",
  },
  {
    icon: "ri-image-line",
    title: "You Receive Photo & Receipt",
    desc: "You receive a photo of the infant receiving your gift, plus an official donation receipt.",
    color: "bg-amber-50",
    iconColor: "text-amber-600",
    num: "3",
  },
  {
    icon: "ri-sparkling-2-line",
    title: "God Blesses Your Family",
    desc: "The infant's thanksgiving and prayers bring God's blessings to every member of your family.",
    color: "bg-yellow-50",
    iconColor: "text-yellow-600",
    num: "4",
  },
];

// ── Thanksgiving Wall data ──
const thanksgivingWall = [
  {
    babyName: "Baby Emma",
    family: "The Johnson Family",
    country: "USA",
    flag: "🇺🇸",
    photo: "https://readdy.ai/api/search-image?query=happy%20blonde%20baby%20girl%20holding%20handmade%20gift%20bracelet%20smiling%20joyful%20warm%20sunlight%20cozy%20home%20family%20love%20portrait&width=400&height=300&seq=thanks-wall1&orientation=landscape",
    giftSent: "Handmade friendship bracelet",
    message: "Thank you from the bottom of our hearts. Your kindness gave Emma warmth and milk for a whole month. God bless your entire family forever.",
    donorName: "Michael R.",
    donorAmount: "$250",
  },
  {
    babyName: "Baby Oliver",
    family: "The Williams Family",
    country: "Australia",
    flag: "🇦🇺",
    photo: "https://readdy.ai/api/search-image?query=cute%20blonde%20baby%20boy%20happy%20laughing%20holding%20colorful%20drawing%20paper%20thank%20you%20gift%20warm%20sunlight%20home%20portrait%20joyful&width=400&height=300&seq=thanks-wall2&orientation=landscape",
    giftSent: "Hand-drawn family portrait",
    message: "We cried tears of joy when the donation arrived. Oliver is healthy and smiling every day now. We pray God blesses your family with abundance.",
    donorName: "Jennifer L.",
    donorAmount: "$100",
  },
  {
    babyName: "Baby Lily",
    family: "The Anderson Family",
    country: "Canada",
    flag: "🇨🇦",
    photo: "https://readdy.ai/api/search-image?query=adorable%20blonde%20baby%20girl%20wrapped%20in%20soft%20handmade%20knitted%20blanket%20smiling%20peacefully%20warm%20cozy%20home%20portrait%20thanksgiving&width=400&height=300&seq=thanks-wall3&orientation=landscape",
    giftSent: "Hand-knitted wool blanket",
    message: "Lily sleeps so peacefully now in her new warm blanket. Your generosity is a miracle for our family. May God's blessings rain upon you always.",
    donorName: "David K.",
    donorAmount: "$500",
  },
  {
    babyName: "Baby Lucas",
    family: "The Mueller Family",
    country: "Germany",
    flag: "🇩🇪",
    photo: "https://readdy.ai/api/search-image?query=happy%20blonde%20baby%20boy%20smiling%20holding%20small%20handmade%20wooden%20toy%20gift%20warm%20sunlight%20home%20family%20portrait%20joyful%20thanksgiving&width=400&height=300&seq=thanks-wall4&orientation=landscape",
    giftSent: "Handcarved wooden blessing toy",
    message: "Lucas laughed for the first time after receiving proper nutrition. You are an angel to our family. We thank God every day for your kindness.",
    donorName: "Sarah M.",
    donorAmount: "$50",
  },
  {
    babyName: "Baby Sophie",
    family: "The Brown Family",
    country: "UK",
    flag: "🇬🇧",
    photo: "https://readdy.ai/api/search-image?query=sweet%20blonde%20baby%20girl%20happy%20smiling%20holding%20small%20handmade%20flower%20bouquet%20gift%20warm%20sunlight%20home%20portrait%20joyful%20love&width=400&height=300&seq=thanks-wall5&orientation=landscape",
    giftSent: "Dried flower blessing bouquet",
    message: "Sophie is growing so strong and healthy. Your donation was the answer to our prayers. God will bless every member of your family with health and happiness.",
    donorName: "Robert T.",
    donorAmount: "$150",
  },
  {
    babyName: "Baby Max",
    family: "The Taylor Family",
    country: "New Zealand",
    flag: "🇳🇿",
    photo: "https://readdy.ai/api/search-image?query=adorable%20blonde%20baby%20boy%20happy%20laughing%20playing%20with%20handmade%20colorful%20gift%20toy%20warm%20sunlight%20home%20family%20portrait%20thanksgiving%20love&width=400&height=300&seq=thanks-wall6&orientation=landscape",
    giftSent: "Handpainted blessing stone",
    message: "Max is our sunshine and your donation kept him warm and fed. We painted a blessing stone with a prayer for your family — may it bring you peace forever.",
    donorName: "Lisa W.",
    donorAmount: "$75",
  },
];

// ── Live donation ticker data ──
const tickerDonations = [
  { name: "James T.", country: "USA", flag: "🇺🇸", amount: "$50", time: "2 min ago" },
  { name: "Yuki M.", country: "Japan", flag: "🇯🇵", amount: "$100", time: "5 min ago" },
  { name: "Sophie R.", country: "France", flag: "🇫🇷", amount: "$25", time: "7 min ago" },
  { name: "Carlos B.", country: "Brazil", flag: "🇧🇷", amount: "$200", time: "9 min ago" },
  { name: "Anna K.", country: "Germany", flag: "🇩🇪", amount: "$75", time: "11 min ago" },
  { name: "Lottery Winner", country: "USA", flag: "🇺🇸", amount: "$1,000", time: "14 min ago" },
  { name: "Wei L.", country: "China", flag: "🇨🇳", amount: "$150", time: "16 min ago" },
  { name: "Priya S.", country: "India", flag: "🇮🇳", amount: "$30", time: "18 min ago" },
  { name: "Tom H.", country: "UK", flag: "🇬🇧", amount: "$250", time: "21 min ago" },
  { name: "Maria G.", country: "Spain", flag: "🇪🇸", amount: "$45", time: "23 min ago" },
  { name: "Rich Donor", country: "Canada", flag: "🇨🇦", amount: "$500", time: "25 min ago" },
  { name: "Hana Y.", country: "South Korea", flag: "🇰🇷", amount: "$60", time: "28 min ago" },
];

// ── Live Donation Ticker Component ──
function LiveDonationTicker() {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [visible, setVisible] = useState(true);
  const [totalToday, setTotalToday] = useState(4820);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setCurrentIdx((prev) => (prev + 1) % tickerDonations.length);
        setTotalToday((prev) => prev + Math.floor(Math.random() * 50 + 10));
        setVisible(true);
      }, 400);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  const current = tickerDonations[currentIdx];

  return (
    <div className="bg-rose-600 text-white py-3 px-6 md:px-12">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
        {/* Live ticker */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-xs font-bold uppercase tracking-widest text-rose-200">Live Donations</span>
          </div>
          <div
            className="flex items-center gap-2 transition-all duration-300"
            style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(-6px)" }}
          >
            <span className="text-lg">{current.flag}</span>
            <span className="text-sm font-bold text-white">{current.name}</span>
            <span className="text-rose-200 text-xs">from {current.country}</span>
            <span className="px-2.5 py-0.5 rounded-full bg-white/20 text-white text-sm font-extrabold">
              {current.amount}
            </span>
            <span className="text-rose-300 text-xs hidden sm:inline">{current.time}</span>
          </div>
        </div>

        {/* Today's total */}
        <div className="flex items-center gap-2 text-sm flex-shrink-0">
          <i className="ri-heart-fill text-rose-300" />
          <span className="text-rose-200 font-medium">Today's total:</span>
          <span className="font-extrabold text-white text-base">${totalToday.toLocaleString()}</span>
          <span className="text-rose-200 text-xs">donated worldwide</span>
        </div>
      </div>
    </div>
  );
}

type PayMethod = "paypal" | "card";

export default function InfantDonationSection() {
  const [selectedAmount, setSelectedAmount] = useState<number | null>(25);
  const [customAmount, setCustomAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [donorMessage, setDonorMessage] = useState("");
  const [payMethod, setPayMethod] = useState<PayMethod>("paypal");
  const [cardNumber, setCardNumber] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCvc, setCardCvc] = useState("");
  const [cardName, setCardName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [liked, setLiked] = useState<Record<number, boolean>>({});
  const [wallLiked, setWallLiked] = useState<Record<number, boolean>>({});
  const wallRef = useRef<HTMLDivElement>(null);

  const finalAmount = customAmount ? Number(customAmount) : selectedAmount;

  const formatCardNumber = (val: string) => {
    const digits = val.replace(/\D/g, "").slice(0, 16);
    return digits.replace(/(.{4})/g, "$1 ").trim();
  };

  const formatExpiry = (val: string) => {
    const digits = val.replace(/\D/g, "").slice(0, 4);
    if (digits.length >= 3) return digits.slice(0, 2) + "/" + digits.slice(2);
    return digits;
  };

  const handleDonate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!finalAmount || finalAmount <= 0) return;
    if (!donorName.trim() || !donorEmail.trim()) return;

    setSubmitting(true);

    // Record donor info via form submission
    const params = new URLSearchParams();
    params.append("donor_name", donorName);
    params.append("email", donorEmail);
    params.append("donation_amount", String(finalAmount));
    params.append("payment_method", payMethod);
    params.append("message", donorMessage);
    try {
      await fetch("https://readdy.ai/api/form/d7e3envsch5bvrijfqs0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
    } catch {
      // continue even if form submission fails
    }

    if (payMethod === "paypal") {
      // Open PayPal donation page with amount
      const paypalUrl = `https://www.paypal.com/donate?business=donations%40starbookprime.com&amount=${finalAmount}&currency_code=USD&item_name=Infant+Happiness+Foundation+Donation&no_note=0&cn=Message+of+Love&no_shipping=1`;
      window.open(paypalUrl, "_blank", "noopener,noreferrer");
    }

    setSubmitting(false);
    setSubmitted(true);
  };

  return (
    <section className="relative overflow-hidden">

      {/* ── FLOATING SHARE BUTTON ── */}
      <ShareFloatingButton />

      {/* ── LIVE DONATION TICKER ── */}
      <LiveDonationTicker />

      {/* ── HERO BANNER ── */}
      <div className="relative w-full h-[380px] sm:h-[460px] md:h-[520px] lg:h-[600px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=adorable%20blonde%20hair%20toddler%20kids%20children%20happy%20smiling%20playing%20outdoors%20sunny%20day%20bright%20warm%20golden%20light%20cheerful%20joyful%20wide%20banner%20portrait%20beautiful%20background&width=1600&height=600&seq=infant-hero-blonde-kids&orientation=landscape"
          alt="Happy blonde children"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-rose-900/80 via-rose-800/60 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-center px-5 sm:px-8 md:px-20">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-bold uppercase tracking-widest mb-4 md:mb-5">
              <i className="ri-heart-fill text-rose-300" />
              Infant Happiness Foundation
              <i className="ri-heart-fill text-rose-300" />
            </div>
            <h2
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-4 md:mb-5 leading-tight"
              style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}
            >
              Make Every Infant
              <br />
              <span className="text-rose-300">Happy &amp; Great</span>
            </h2>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 leading-relaxed mb-5 md:mb-7 font-medium max-w-xl">
              Welcome <span className="text-yellow-300 font-bold">lottery winners</span> &amp; <span className="text-amber-300 font-bold">generous hearts</span> — donate a small amount for infants who need <span className="text-rose-300 font-bold">love, milk &amp; clothes</span>. Every donor receives a handmade gift from the infant's family. <span className="font-bold text-white">We are one big family.</span>
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => document.getElementById("infant-donation-form")?.scrollIntoView({ behavior: "smooth" })}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 md:px-7 md:py-3.5 rounded-full font-extrabold text-sm bg-rose-500 text-white hover:bg-rose-400 transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-heart-fill" /> Donate Now — Change a Life
              </button>
              <button
                onClick={() => wallRef.current?.scrollIntoView({ behavior: "smooth" })}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white/15 backdrop-blur-sm text-white text-sm font-semibold border border-white/30 hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-sparkling-2-fill text-yellow-300" />
                See Thanksgiving Wall
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── THANK-YOU MARQUEE ── */}
      <ThankYouMarquee />

      {/* ── IMPACT STATS STRIP ── */}
      <div className="bg-white border-b border-rose-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {impactStats.map((stat) => (
              <div key={stat.label} className={`rounded-2xl p-5 text-center ${stat.bg} border border-rose-50`}>
                <div className={`w-10 h-10 flex items-center justify-center mx-auto mb-2 text-2xl ${stat.color}`}>
                  <i className={stat.icon} />
                </div>
                <div className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-0.5">{stat.value}</div>
                <div className="text-xs text-gray-500 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── MAIN BODY ── */}
      <div className="bg-gradient-to-b from-rose-50/60 via-orange-50/40 to-white py-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">

          {/* How It Works */}
          <div className="mb-12 md:mb-16">
            <div className="text-center mb-8 md:mb-10">
              <h3
                className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-900 mb-3"
                style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}
              >
                How the Gift Exchange Works
              </h3>
              <p className="text-gray-500 text-sm md:text-base max-w-xl mx-auto">
                A beautiful circle of love — you give, they give back, and God blesses everyone
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {howItWorks.map((step, i) => (
                <div key={step.title} className={`rounded-2xl p-5 md:p-7 border border-rose-100 ${step.color} flex flex-col items-center text-center relative`}>
                  {i < howItWorks.length - 1 && (
                    <div className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-6 h-6 flex items-center justify-center text-rose-300">
                      <i className="ri-arrow-right-s-line text-2xl" />
                    </div>
                  )}
                  <div className={`w-14 h-14 flex items-center justify-center rounded-full bg-white mb-4 text-3xl ${step.iconColor}`}>
                    <i className={step.icon} />
                  </div>
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-rose-500 text-white text-xs font-bold mb-3">
                    {step.num}
                  </div>
                  <h4 className="text-base font-extrabold text-gray-900 mb-2">{step.title}</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Infant Stories + Donation Form */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">

            {/* Infant Stories */}
            <div>
              <h3
                className="text-2xl font-extrabold text-gray-900 mb-2"
                style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}
              >
                Infants Waiting for Your Love
              </h3>
              <p className="text-gray-500 text-sm mb-6">Each infant's family will send you a handmade gift in return</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {infantStories.map((infant, i) => (
                  <div
                    key={infant.name}
                    className="bg-white rounded-2xl overflow-hidden border border-rose-100 hover:border-rose-300 transition-all hover:-translate-y-1 cursor-pointer"
                  >
                    <div className="relative w-full h-44 overflow-hidden">
                      <img
                        src={infant.image}
                        alt={infant.name}
                        className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${infant.statusColor}`}>
                          {infant.status}
                        </span>
                      </div>
                      <div className="absolute top-3 right-3 text-xl">{infant.flag}</div>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-extrabold text-gray-900 text-sm">{infant.name}</span>
                        <span className="text-xs text-gray-400">{infant.age} · {infant.country}</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-rose-500 font-semibold mb-2">
                        <i className="ri-heart-line" /> Needs: {infant.need}
                      </div>
                      <p className="text-xs text-gray-600 leading-relaxed italic mb-3">"{infant.message}"</p>
                      <div className="flex items-center gap-3 pt-3 border-t border-gray-50">
                        <button
                          onClick={() => setLiked((prev) => ({ ...prev, [i]: !prev[i] }))}
                          className={`flex items-center gap-1 text-xs transition-colors cursor-pointer ${liked[i] ? "text-rose-500" : "text-gray-400 hover:text-rose-500"}`}
                        >
                          <i className={liked[i] ? "ri-heart-fill" : "ri-heart-line"} /> Love
                        </button>
                        <button className="flex items-center gap-1 text-xs text-gray-400 hover:text-orange-500 transition-colors cursor-pointer">
                          <i className="ri-share-forward-line" /> Share
                        </button>
                        <button
                          onClick={() => {
                            setSelectedAmount(25);
                            document.getElementById("infant-donation-form")?.scrollIntoView({ behavior: "smooth" });
                          }}
                          className="ml-auto flex items-center gap-1 text-xs font-bold text-rose-500 hover:text-rose-600 cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-gift-2-line" /> Donate Now
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Donation Form */}
            <div id="infant-donation-form">
              <h3
                className="text-2xl font-extrabold text-gray-900 mb-2"
                style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}
              >
                Make Your Donation
              </h3>
              <p className="text-gray-500 text-sm mb-6">100% goes directly to infants in need — you'll receive a gift in return</p>

              {submitted ? (
                <div className="bg-white rounded-2xl border border-rose-200 p-10 text-center">
                  <div className="w-20 h-20 flex items-center justify-center mx-auto mb-5 rounded-full bg-rose-50 text-5xl text-rose-500">
                    <i className="ri-heart-fill" />
                  </div>
                  <h4
                    className="text-2xl font-extrabold text-gray-900 mb-3"
                    style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}
                  >
                    Thank You, Generous Soul!
                  </h4>
                  <p className="text-gray-600 leading-relaxed mb-5 text-sm">
                    {payMethod === "paypal"
                      ? "Your PayPal payment window has opened. Once completed, the infant's family will send you a small handmade gift, a photo of the infant receiving your love, and an official receipt to your email."
                      : "Your card donation has been submitted. The infant's family will send you a small handmade gift, a photo of the infant receiving your love, and an official receipt to your email."}
                  </p>
                  <div className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-sm font-bold mb-5">
                    <i className="ri-sparkling-2-fill text-yellow-500" />
                    God Bless You and Your Entire Family
                  </div>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setDonorName("");
                      setDonorEmail("");
                      setDonorMessage("");
                      setSelectedAmount(25);
                      setCustomAmount("");
                      setCardNumber("");
                      setCardExpiry("");
                      setCardCvc("");
                      setCardName("");
                    }}
                    className="block w-full py-3 rounded-xl bg-rose-500 text-white font-bold text-sm hover:bg-rose-600 transition-colors cursor-pointer"
                  >
                    Donate Again
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form
                  onSubmit={handleDonate}
                  className="bg-white rounded-2xl border border-rose-100 p-7 space-y-5"
                >
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-3">
                      Select Donation Amount (USD)
                    </label>
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {donationAmounts.map((amt) => (
                        <button
                          key={amt}
                          type="button"
                          onClick={() => { setSelectedAmount(amt); setCustomAmount(""); }}
                          className={`py-2.5 rounded-xl text-sm font-bold border-2 transition-all cursor-pointer whitespace-nowrap ${
                            selectedAmount === amt && !customAmount
                              ? "border-rose-500 bg-rose-50 text-rose-600"
                              : "border-gray-200 text-gray-700 hover:border-rose-300"
                          }`}
                        >
                          ${amt}
                        </button>
                      ))}
                    </div>
                    <input
                      type="number"
                      name="donation_amount"
                      placeholder="Or enter custom amount..."
                      value={customAmount}
                      onChange={(e) => { setCustomAmount(e.target.value); setSelectedAmount(null); }}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-rose-400 text-gray-700"
                      min="1"
                    />
                    {finalAmount && finalAmount > 0 && (
                      <div className="mt-2 text-xs text-rose-500 font-semibold flex items-center gap-1">
                        <i className="ri-heart-fill" />
                        Your ${finalAmount} donation will provide milk, clothes, and love for an infant in need.
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1.5">
                      Your Name <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="donor_name"
                      required
                      value={donorName}
                      onChange={(e) => setDonorName(e.target.value)}
                      placeholder="Your full name"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-rose-400 text-gray-700"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1.5">
                      Email Address <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={donorEmail}
                      onChange={(e) => setDonorEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-rose-400 text-gray-700"
                    />
                    <p className="text-xs text-gray-400 mt-1">Your receipt and infant gift photo will be sent here.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1.5">
                      Message of Love <span className="text-gray-400 font-normal">(Optional)</span>
                    </label>
                    <textarea
                      name="message"
                      value={donorMessage}
                      onChange={(e) => {
                        if (e.target.value.length <= 500) setDonorMessage(e.target.value);
                      }}
                      placeholder="Write a blessing or message for the infant and their family..."
                      rows={3}
                      maxLength={500}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-rose-400 text-gray-700 resize-none"
                    />
                    <div className="text-xs text-gray-400 text-right">{donorMessage.length}/500</div>
                  </div>

                  <div className="rounded-xl bg-gradient-to-r from-rose-50 to-orange-50 border border-rose-100 p-4">
                    <div className="text-xs font-bold text-rose-700 mb-2.5 uppercase tracking-wide flex items-center gap-1">
                      <i className="ri-gift-2-fill text-orange-500" /> What You Receive in Return
                    </div>
                    <ul className="space-y-1.5">
                      {[
                        "A small handmade gift from the infant's family",
                        "A photo of the infant receiving your donation",
                        "An official donation receipt via email",
                        "God's blessings for your entire family",
                      ].map((item) => (
                        <li key={item} className="flex items-start gap-2 text-xs text-rose-800">
                          <i className="ri-heart-fill text-rose-400 mt-0.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Payment Method Selector */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-3">Payment Method</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setPayMethod("paypal")}
                        className={`flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-bold text-sm transition-all cursor-pointer ${payMethod === "paypal" ? "border-[#003087] bg-[#FFC439]/10" : "border-gray-200 hover:border-gray-300"}`}
                      >
                        <i className="ri-paypal-fill text-[#003087] text-lg" />
                        <span className="text-[#003087]" style={{ fontFamily: "'Arial', sans-serif", fontWeight: 800 }}>
                          Pay<span style={{ color: "#009cde" }}>Pal</span>
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setPayMethod("card")}
                        className={`flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-bold text-sm transition-all cursor-pointer ${payMethod === "card" ? "border-rose-500 bg-rose-50" : "border-gray-200 hover:border-gray-300"}`}
                      >
                        <i className="ri-bank-card-line text-gray-700 text-lg" />
                        <span className="text-gray-700">Credit / Debit Card</span>
                      </button>
                    </div>
                    {/* Card logos */}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {payMethod === "card" ? (
                        <>
                          <span className="text-xs text-gray-400">Accepted:</span>
                          {["ri-visa-line", "ri-mastercard-line"].map((icon) => (
                            <i key={icon} className={`${icon} text-2xl text-gray-500`} />
                          ))}
                          <span className="text-xs font-bold text-gray-500 border border-gray-300 px-1.5 py-0.5 rounded">AMEX</span>
                          <span className="text-xs font-bold text-gray-500 border border-gray-300 px-1.5 py-0.5 rounded">DISCOVER</span>
                        </>
                      ) : (
                        <span className="text-xs text-gray-400">No PayPal account needed — pay with card via PayPal checkout</span>
                      )}
                    </div>
                  </div>

                  {/* Card Fields */}
                  {payMethod === "card" && (
                    <div className="space-y-3 bg-gray-50 rounded-xl p-4 border border-gray-100">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Cardholder Name *</label>
                        <input
                          type="text"
                          value={cardName}
                          onChange={(e) => setCardName(e.target.value)}
                          placeholder="Name on card"
                          required={payMethod === "card"}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-rose-400 bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Card Number *</label>
                        <div className="relative">
                          <i className="ri-bank-card-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                          <input
                            type="text"
                            value={cardNumber}
                            onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                            placeholder="1234 5678 9012 3456"
                            maxLength={19}
                            required={payMethod === "card"}
                            className="w-full pl-9 pr-3 border border-gray-200 rounded-lg py-2.5 text-sm outline-none focus:border-rose-400 bg-white"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Expiry Date *</label>
                          <input
                            type="text"
                            value={cardExpiry}
                            onChange={(e) => setCardExpiry(formatExpiry(e.target.value))}
                            placeholder="MM/YY"
                            maxLength={5}
                            required={payMethod === "card"}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-rose-400 bg-white"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1.5">CVC / CVV *</label>
                          <div className="relative">
                            <input
                              type="text"
                              value={cardCvc}
                              onChange={(e) => setCardCvc(e.target.value.replace(/\D/g, "").slice(0, 4))}
                              placeholder="123"
                              maxLength={4}
                              required={payMethod === "card"}
                              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-rose-400 bg-white"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <i className="ri-shield-check-line text-green-500" />
                        256-bit SSL encrypted · Your card info is never stored
                      </div>
                    </div>
                  )}

                  {/* Donate Button */}
                  {payMethod === "paypal" ? (
                    <button
                      type="submit"
                      disabled={submitting || !finalAmount || !donorName.trim() || !donorEmail.trim()}
                      className="w-full py-4 rounded-xl font-extrabold text-base transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-3"
                      style={{ background: "linear-gradient(135deg, #FFC439 0%, #FFB800 100%)", color: "#003087" }}
                    >
                      {submitting ? (
                        <span className="flex items-center gap-2">
                          <i className="ri-loader-4-line animate-spin" /> Opening PayPal...
                        </span>
                      ) : (
                        <>
                          <i className="ri-paypal-fill text-xl" />
                          <span>Donate {finalAmount ? `$${finalAmount}` : ""} via PayPal</span>
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={submitting || !finalAmount || !donorName.trim() || !donorEmail.trim() || !cardNumber || !cardExpiry || !cardCvc || !cardName}
                      className="w-full py-4 rounded-xl font-extrabold text-base bg-rose-500 text-white transition-all hover:bg-rose-600 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-3"
                    >
                      {submitting ? (
                        <span className="flex items-center gap-2">
                          <i className="ri-loader-4-line animate-spin" /> Processing...
                        </span>
                      ) : (
                        <>
                          <i className="ri-bank-card-line text-xl" />
                          <span>Donate {finalAmount ? `$${finalAmount}` : ""} Securely</span>
                        </>
                      )}
                    </button>
                  )}

                  <p className="text-center text-xs text-gray-400">
                    <i className="ri-shield-check-line text-green-500" /> Secure payment · 100% goes to infants in need · SSL encrypted
                  </p>
                </form>
              )}
            </div>
          </div>

          {/* Recent Donors */}
          <div className="mb-16">
            <div className="text-center mb-8">
              <h3
                className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-2"
                style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}
              >
                Our Generous Donors &amp; Their Gifts
              </h3>
              <p className="text-gray-500 text-sm">Real donors who gave love — and received love back</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {recentDonors.map((donor) => (
                <div key={donor.name} className="bg-white rounded-2xl border border-rose-100 p-6 hover:border-rose-300 transition-all">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                      <img src={donor.avatar} alt={donor.name} className="w-full h-full object-cover object-top" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-extrabold text-gray-900 text-sm">{donor.name}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${donor.badgeColor}`}>{donor.badge}</span>
                      </div>
                      <div className="text-green-600 font-bold text-sm">{donor.amount} donated</div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 italic leading-relaxed mb-3">"{donor.message}"</p>
                  <div className="flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-100 p-3">
                    <i className="ri-gift-2-fill text-orange-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-800 font-medium leading-relaxed">{donor.gift}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── THANKSGIVING WALL ── */}
          <div ref={wallRef} className="mb-12 md:mb-16">
            {/* Header */}
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-amber-100 text-amber-700 text-xs font-bold uppercase tracking-widest mb-4">
                <i className="ri-sparkling-2-fill text-yellow-500" />
                Thanksgiving Wall
                <i className="ri-sparkling-2-fill text-yellow-500" />
              </div>
              <h3
                className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-3"
                style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}
              >
                Thank-You Photos &amp; Messages
                <br />
                <span className="text-rose-500">From Infant Families</span>
              </h3>
              <p className="text-gray-500 text-base max-w-2xl mx-auto leading-relaxed">
                Every infant family sends a photo of their baby receiving your gift, along with a heartfelt message of thanksgiving. These are real moments of love and gratitude — God bless every donor.
              </p>
            </div>

            {/* Wall grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {thanksgivingWall.map((item, i) => (
                <div
                  key={item.babyName}
                  className="bg-white rounded-2xl overflow-hidden border border-amber-100 hover:border-amber-300 transition-all hover:-translate-y-1"
                >
                  {/* Photo */}
                  <div className="relative w-full h-48 overflow-hidden">
                    <img
                      src={item.photo}
                      alt={`${item.babyName} thanksgiving`}
                      className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                    />
                    {/* Overlay badge */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                    <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
                      <div>
                        <div className="text-white font-extrabold text-sm leading-tight">{item.babyName}</div>
                        <div className="text-white/80 text-xs">{item.flag} {item.country}</div>
                      </div>
                      <span className="px-2.5 py-1 rounded-full bg-amber-400 text-amber-900 text-xs font-bold whitespace-nowrap">
                        <i className="ri-gift-2-fill mr-1" />{item.giftSent}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    {/* Family name */}
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-rose-100 text-rose-500 flex-shrink-0">
                        <i className="ri-home-heart-line text-sm" />
                      </div>
                      <div>
                        <div className="text-xs font-bold text-gray-900">{item.family}</div>
                        <div className="text-xs text-gray-400">Sent to donor: {item.donorName} · {item.donorAmount}</div>
                      </div>
                    </div>

                    {/* Message */}
                    <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 mb-4">
                      <i className="ri-double-quotes-l text-amber-300 text-lg leading-none" />
                      <p className="text-xs text-amber-900 leading-relaxed italic mt-1">{item.message}</p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                      <button
                        onClick={() => setWallLiked((prev) => ({ ...prev, [i]: !prev[i] }))}
                        className={`flex items-center gap-1 transition-colors cursor-pointer ${wallLiked[i] ? "text-rose-500" : "hover:text-rose-500"}`}
                      >
                        <i className={wallLiked[i] ? "ri-heart-fill" : "ri-heart-line"} />
                        <span>{wallLiked[i] ? "Loved" : "Love"}</span>
                      </button>
                      <button className="flex items-center gap-1 hover:text-orange-500 transition-colors cursor-pointer">
                        <i className="ri-share-forward-line" />
                        Share
                      </button>
                      <div className="ml-auto flex items-center gap-1 text-amber-600 font-semibold">
                        <i className="ri-sparkling-2-fill text-yellow-500" />
                        <span>God Bless</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Wall CTA */}
            <div className="mt-8 text-center">
              <div className="inline-flex items-center gap-3 px-8 py-4 rounded-2xl bg-gradient-to-r from-amber-50 to-rose-50 border border-amber-200">
                <i className="ri-heart-fill text-rose-400 text-xl" />
                <p className="text-sm text-gray-700 font-medium">
                  <span className="font-bold text-gray-900">Your photo could be here too.</span> Donate today and receive a real thank-you photo from an infant family.
                </p>
                <button
                  onClick={() => document.getElementById("infant-donation-form")?.scrollIntoView({ behavior: "smooth" })}
                  className="flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-rose-500 text-white text-xs font-bold hover:bg-rose-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-heart-fill" />
                  Donate Now
                </button>
              </div>
            </div>
          </div>

          {/* Bottom CTA Banner */}
          <div
            className="rounded-3xl p-7 sm:p-10 md:p-14 text-center relative overflow-hidden"
            style={{ background: "linear-gradient(135deg, #fff0f3 0%, #ffe4e8 40%, #ffd6dc 70%, #ffe4e8 100%)" }}
          >
            <div className="absolute inset-0 opacity-10 pointer-events-none">
              <img
                src="https://readdy.ai/api/search-image?query=soft%20pink%20rose%20petals%20hearts%20gentle%20watercolor%20background%20warm%20light%20pastel%20elegant%20texture&width=1200&height=400&seq=infant-cta-bg2&orientation=landscape"
                alt=""
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="relative z-10">
              <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center mx-auto mb-4 text-4xl md:text-5xl text-rose-400">
                <i className="ri-heart-fill" />
              </div>
              <h3
                className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold mb-4 text-rose-800 leading-tight"
                style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}
              >
                Together We Are{" "}
                <span className="text-rose-500">One Big Family</span>
              </h3>
              <p
                className="text-base md:text-lg lg:text-xl text-rose-700 max-w-3xl mx-auto mb-6 md:mb-8 font-semibold leading-relaxed"
                style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}
              >
                Every infant who receives your love will be thankful, and God will bless every member of your family. Share this with your friends — together, let's make every infant in the world happy and great.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center flex-wrap">
                <button
                  onClick={() => document.getElementById("infant-donation-form")?.scrollIntoView({ behavior: "smooth" })}
                  className="inline-flex items-center justify-center gap-2 px-6 md:px-8 py-3.5 md:py-4 rounded-full font-extrabold text-sm md:text-base bg-rose-500 text-white hover:bg-rose-600 transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-heart-fill" />
                  Donate Now — Make an Infant Happy
                </button>
                <Link
                  to="/infant-donation"
                  className="inline-flex items-center justify-center gap-2 px-6 md:px-8 py-3.5 md:py-4 rounded-full font-extrabold text-sm md:text-base bg-amber-400 text-amber-900 hover:bg-amber-300 transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-trophy-fill" />
                  View Hall of Fame
                </Link>
                <Link
                  to="/social"
                  className="inline-flex items-center justify-center gap-2 px-6 md:px-8 py-3.5 md:py-4 rounded-full font-extrabold text-sm md:text-base bg-white text-rose-600 border-2 border-rose-200 hover:border-rose-400 transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-share-forward-line" />
                  Share With Friends
                </Link>
              </div>
              <div className="mt-6 md:mt-8 flex flex-wrap items-center justify-center gap-4 md:gap-6 text-rose-600 text-xs md:text-sm font-semibold">
                <span className="flex items-center gap-1">
                  <i className="ri-heart-fill text-rose-400" /> 12,480+ Infants Helped
                </span>
                <span className="w-1 h-1 rounded-full bg-rose-300 hidden sm:block" />
                <span className="flex items-center gap-1">
                  <i className="ri-gift-2-fill text-orange-400" /> Gifts Exchanged Worldwide
                </span>
                <span className="w-1 h-1 rounded-full bg-rose-300 hidden sm:block" />
                <span className="flex items-center gap-1">
                  <i className="ri-sparkling-2-fill text-yellow-500" /> God Bless Every Donor
                </span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
